import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";

const FIELDS = [
    ["bank_name", "Bank name", "e.g. Emirates NBD"],
    ["bank_account_holder", "Account holder", "Full name as on bank"],
    ["bank_account_number", "Account number", ""],
    ["bank_iban", "IBAN", "AE07 ..."],
    ["bank_swift", "SWIFT / BIC", ""],
    ["bank_routing_number", "Routing number", ""],
];

export default function TechProfile() {
    const { user } = useAuth();
    const [bank, setBank] = useState({});
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/users/me/bank");
                if (!cancelled) setBank(data || {});
            } catch (e) {
                /* ignore */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const save = async () => {
        setBusy(true);
        try {
            await api.patch("/users/me/bank", bank);
            toast.success("Bank details saved");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-5">
            <div className="lx-label text-[#B2912A]">Profile</div>
            <div className="bg-white p-6 text-center">
                <div className="w-24 h-24 mx-auto mb-4 bg-[#F3F2F0] overflow-hidden rounded-full">
                    {user?.avatar_url && (
                        <img
                            src={user.avatar_url}
                            alt=""
                            className="w-full h-full object-cover"
                        />
                    )}
                </div>
                <div className="font-serif text-2xl text-[#180A21]">{user?.name}</div>
                <div className="text-sm text-[#555] mt-1">{user?.email}</div>
                <div className="lx-gold-text font-serif text-3xl mt-4">
                    ★ {user?.rating_avg?.toFixed(1)}
                </div>
                <div className="text-xs text-[#555]">
                    {user?.rating_count} customer reviews
                </div>
            </div>

            <div className="bg-white p-5 grid grid-cols-2 gap-4">
                <Stat label="Completed" value={user?.completed_jobs || 0} />
                <Stat label="Cancelled" value={user?.cancelled_jobs || 0} />
                <Stat label="KYC" value={user?.kyc_status || "—"} />
                <Stat label="City" value={user?.base_city || "—"} />
            </div>

            <div className="bg-white p-5 space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="lx-label text-[#B2912A]">Payout bank details</div>
                        <div className="text-xs text-[#555] mt-1">
                            Required to receive weekly payouts.
                        </div>
                    </div>
                </div>
                {FIELDS.map(([key, label, hint]) => (
                    <div key={key}>
                        <label className="lx-label text-[#555] mb-1.5 block">{label}</label>
                        <input
                            data-testid={`tech-${key.replace(/_/g, "-")}`}
                            value={bank[key] || ""}
                            onChange={(e) => setBank({ ...bank, [key]: e.target.value })}
                            placeholder={hint}
                            className="w-full p-2.5 border border-[#E5E2DC] text-sm focus:border-[#D4AF37] focus:outline-none"
                        />
                    </div>
                ))}
                <button
                    data-testid="tech-bank-save"
                    onClick={save}
                    disabled={busy}
                    className="lx-btn-gold w-full py-3 text-sm font-semibold disabled:opacity-50"
                >
                    {busy ? "Saving…" : "Save bank details"}
                </button>
            </div>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div>
            <div className="lx-label text-[#555]">{label}</div>
            <div className="font-serif text-xl text-[#180A21] capitalize">{value}</div>
        </div>
    );
}
